import { stringify } from 'query-string'
import { ExtendedRequestInfo, FetchResponse } from './fetch/types'
import { HttpError, HttpErrorOptions } from 'ra-core'
import { osFetch } from './osFetch'

export { osFetch } from './osFetch'

export const fetchJson = async (url: RequestInfo, options: ExtendedRequestInfo) => {
  if (!options) options = {}
  options.format = 'json'
  return await fetchRequest(url, options)
}

export const fetchBlob = async (url: RequestInfo, options: ExtendedRequestInfo = {}) => {
  options.format = 'blob'
  return await fetchRequest(url, options)
}

export const fetchRequest = async (
  req: RequestInfo,
  options?: ExtendedRequestInfo,
  onSuccess?: (res: any, requestExtras?: RequestInit) => void
): Promise<FetchResponse> => {
  // Capture request URL and method so they can be attached to any HttpError we
  // construct below. Consumers (notably auth-failure Sentry captures) use these
  // to identify which endpoint produced a 401/403 without having to thread the
  // request object through.
  const errorOptions: HttpErrorOptions = {
    url: typeof req === 'string' ? req : (req as Request).url,
    method: typeof req === 'string' ? options?.method || 'GET' : (req as Request).method || 'GET',
  }
  return osFetch(req, options)
    .then(async (response) => {
      let format = options?.format
      if (!response.ok) {
        if (response.headers.get('content-type') === 'application/json') {
          // If an error contains json, continue down the normal format=json path
          // as the error content could important for the caller to understand
          format = 'json'
        } else {
          throw new HttpError(response.statusText, response.status, null, errorOptions)
        }
      }

      // Handle different response formats
      if (format === 'blob') {
        const blob = await response.blob()
        return {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          body: blob,
          // For blob responses, we don't try to parse json
          json: null,
        }
      }

      const text = await response.text()
      return {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        body: text,
      }
    })
    .then(({ status, statusText, headers, body, json: presetJson }) => {
      // If we already have json set (e.g. from blob handling), use that
      let json: any = presetJson

      // Otherwise try to parse body as JSON
      if (json === undefined) {
        try {
          json = JSON.parse(body as string)
        } catch (e) {
          // not json, no big deal
          //if successful then return the raw body instead
          //avoid keeping raw error body if not json to avoid unexpected behavior due to error messages
          if (status >= 200 && status < 300) {
            json = body
          }
        }
      }

      if (status >= 200 && status < 300) {
        if (onSuccess) {
          onSuccess(json, options)
        }

        return { status, headers, body, json }
      } else if (status === 503) {
        //@TODO: Use Redux to broadcast message instead of this hack
        // @ts-ignore
        window.setMaintenanceMode(true)
        // @ts-ignore
        return Promise.reject(new HttpError('Maintenance In Progress', 503, {}, errorOptions))
      } else {
        const messageExtracted = json ? json.error_msg || extractErrorsAsMessage(json) : 'Internal Server Error'
        return Promise.reject(new HttpError(messageExtracted, status, json, errorOptions))
      }
    })
    .catch((error) => {
      if (error.name === 'AbortError') {
        return Promise.reject(error)
      } else {
        if (error.body && error.body.progress) {
          console.log('Calculation Still in Progress:', error.body.progress)
        } else {
          console.log('fetch.js: There has been a problem with your fetch operation: ', error.message)
        }
        return Promise.reject(
          new HttpError(
            error && error.message && error.message !== 'Failed to fetch'
              ? error.message
              : 'ra.notification.connection_error',
            error.status,
            error.body ? error.body : undefined,
            errorOptions
          )
        )
      }
    })
}

export const queryParameters = stringify

const isValidObject = (value) => {
  if (!value) {
    return false
  }

  const isArray = Array.isArray(value)
  const isBuffer = Buffer.isBuffer(value)
  const isObject = Object.prototype.toString.call(value) === '[object Object]'
  const hasKeys = !!Object.keys(value).length

  return !isArray && !isBuffer && isObject && hasKeys
}

export const flattenObject = (value: any, path: any[] = []) => {
  if (isValidObject(value)) {
    return Object.assign({}, ...Object.keys(value).map((key) => flattenObject(value[key], path.concat([key]))))
  } else {
    return path.length ? { [path.join('.')]: value } : value
  }
}
const upperFirst = (str) => {
  try {
    return str[0].toUpperCase() + str.slice(1)
  } catch (e) {
    return str
  }
}

const stripPeriodFromEnd = (str) => {
  try {
    return str.slice(-1) === '.' ? str.slice(0, -1) : str
  } catch (e) {
    return str
  }
}

const stripPeriodFromEndUpperFirst = (str) => {
  return upperFirst(stripPeriodFromEnd(str)).split('_').join(' ')
}

export const extractErrorsAsMessage = (json): string => {
  const messageParts: string[] = []
  for (const key in json) {
    // If field errors is an array combine with commas
    let item

    const fieldNamePrefix = key !== 'non_field_errors' ? stripPeriodFromEndUpperFirst(key) + ': ' : ''
    const value = json[key]

    if (value === null) {
      continue
    } else if (Array.isArray(value) && value.length > 0) {
      if (typeof value[0] === 'object') {
        // assume nested object, only use first item and join it's strings.
        // Using Object.values would be cleaner here but per tsconfig we are targeting ES5.
        item =
          fieldNamePrefix +
          Object.keys(value[0])
            .map((key) => value[0][key][0])
            .join(', ')
      } else {
        item = fieldNamePrefix + value.join(', ')
      }
    } else if (typeof value === 'object' && value !== null) {
      // Handle nested objects by recursively extracting their error messages
      item = extractErrorsAsMessage(value)
    } else {
      item = fieldNamePrefix + value
    }

    if (item.constructor === Boolean) {
      //continue
    } else {
      messageParts.push(stripPeriodFromEndUpperFirst(item))
    }
  }

  return messageParts.map((msg) => stripPeriodFromEndUpperFirst(msg)).join('. ')
}
